import type { DetentionPeriod } from "../core/stateLoader.js";

// ============================================================================
// Type Definitions
// ============================================================================

export type PathwayPattern = {
  name: string;
  description?: string;
  steps: PatternStep[];
  measurements?: Measurement[];
  anchorStart?: boolean; // ^: match must start at the first period of the input sequence
  anchorEnd?: boolean; // $: match must consume through the last period of the input sequence
};

export type PatternStep = {
  id?: string; // For referencing in measurements
  match: StatusMatch;
  duration?: {
    min?: number; // days
    max?: number; // days
    minExclusive?: boolean; // true for > instead of >=
    maxExclusive?: boolean; // true for < instead of <=
  };
  quantifier: "one" | "zeroOrOne" | "oneOrMore" | "zeroOrMore";
  greedy: boolean;
};

export type StatusMatch =
  | { type: "exact"; status: string }
  | { type: "any" }
  | { type: "anyExcept"; exclude: string[] }
  | { type: "oneOf"; statuses: string[] };

export type Measurement = {
  name: string;
  type: "duration" | "count";
  from: string; // step id
  to: string; // step id
  fromEdge?: "start" | "end";
  toEdge?: "start" | "end";
  countOf?: string; // step id to count (for type: "count")
};

export type PathwayMatch = {
  matched: boolean;
  stepMatches: StepMatch[];
  measurements: MeasurementResult[];
};

export type StepMatch = {
  stepId?: string;
  periods: DetentionPeriod[];
  startDate: Date;
  endDate: Date;
};

export type MeasurementResult = {
  name: string;
  type: "duration" | "count";
  value: number; // days for duration, count for count
  days: number; // @deprecated - use value instead, kept for backwards compatibility
  fromDate: Date;
  toDate: Date;
  // How the measured (from) episode ended; "expired" = system auto-lapse with
  // no recorded discharge (a data-capture artifact, not a clinical ending).
  endStatus?: DetentionPeriod["completionStatus"];
};

// Known valid statuses for error suggestions (includes shorthand forms)
const VALID_STATUSES = [
  // Canonical forms
  "Not Detained",
  "Section 136",
  "Section 2",
  "Section 3",
  "Section 4",
  "Section 5(2)",
  "Section 5(4)",
  "Section 17A",
  "Section 17A (Recalled)",
  "Deceased",
  // CTO aliases
  "CTO",
  "CTO (Recalled)",
  "CTO-R",
  // Numeric shorthand
  "2",
  "3",
  "4",
  "5(2)",
  "5(4)",
  "17A",
  "17A-R",
  "136",
  // s-prefixed shorthand (lowercase)
  "s2",
  "s3",
  "s4",
  "s5(2)",
  "s5(4)",
  "s17a",
  "s17a-r",
  "s136",
  // S-prefixed shorthand (uppercase)
  "S2",
  "S3",
  "S4",
  "S5(2)",
  "S5(4)",
  "S17A",
  "S17A-R",
  "S136",
  // Not Detained shorthand
  "ND",
  "nd",
  // Deceased aliases
  "deceased",
  "dead",
  "died",
  "death",
];

export type DslValidationError = {
  line: number;
  lineContent: string;
  message: string;
  friendlyMessage: string;
  suggestion?: string;
  highlight?: string; // The specific part of the line to highlight/bold
};

export type DslValidationResult = {
  valid: boolean;
  errors: DslValidationError[];
  pattern: PathwayPattern | null;
};

/**
 * Capture group `n` of a successful match. Every group read through here is
 * mandatory in its pattern, so a match implies a capture; an absent one means
 * the pattern and the call reading it have drifted apart. Optional groups —
 * `(?:…)?` — must be read directly instead, so that the absent case stays
 * visible at the call site.
 */
function group(match: RegExpMatchArray, n: number): string {
  const captured = match[n];
  if (captured === undefined) {
    throw new Error(`Regex matched without capture group ${String(n)}`);
  }
  return captured;
}

/**
 * Validate a DSL string and return friendly error messages with suggestions
 */
export function validatePathwayDsl(dsl: string): DslValidationResult {
  const errors: DslValidationError[] = [];
  const lines = dsl.split("\n");
  const nonEmptyLines = lines
    .map((l, i) => ({ content: l.trim(), lineNum: i + 1 }))
    .filter((l) => l.content && !l.content.startsWith("#"));

  let section: "header" | "steps" | "measures" = "header";
  let hasSteps = false;
  const stepIds: string[] = [];
  const stepQuantifiers = new Map<string, PatternStep["quantifier"]>();

  // Ordered step-section line numbers (excludes measures) so ^/$ anchors can be
  // validated as sitting on the first / last step respectively.
  const stepSectionLineNums: number[] = [];
  {
    let sec: "header" | "steps" | "measures" = "header";
    for (const { content, lineNum } of nonEmptyLines) {
      if (content === "---") {
        sec = sec === "header" ? "steps" : "measures";
        continue;
      }
      if (
        sec === "steps" &&
        !content.startsWith("measure:") &&
        !content.startsWith("count:") &&
        !content.startsWith("count ")
      ) {
        stepSectionLineNums.push(lineNum);
      }
    }
  }
  const firstStepLineNum = stepSectionLineNums[0];
  const lastStepLineNum = stepSectionLineNums[stepSectionLineNums.length - 1];

  for (const { content: line, lineNum } of nonEmptyLines) {
    if (line === "---") {
      section = section === "header" ? "steps" : "measures";
      continue;
    }

    if (section === "header") {
      // Header is just the name, always valid
      continue;
    }

    if (
      section === "measures" ||
      line.startsWith("measure:") ||
      line.startsWith("count:") ||
      line.startsWith("count ")
    ) {
      // Validate measurement/count syntax
      const isCount = line.startsWith("count:") || line.startsWith("count ");
      const parsed = parseMeasurementLine(line);

      if (!parsed) {
        errors.push({
          line: lineNum,
          lineContent: line,
          message: isCount
            ? "Invalid count syntax"
            : "Invalid measurement syntax",
          friendlyMessage: isCount
            ? "This count definition isn't quite right."
            : "This measurement definition isn't quite right.",
          suggestion: isCount
            ? 'Try: "count: Recalls: recall in ctoStart -> ctoEnd" or "count recalls between start, end"'
            : 'Try: "measure: name: @stepA -> @stepB" or "measure: @stepA.end -> @stepB.start"',
        });
      } else {
        // Check if referenced step IDs exist
        if (!stepIds.includes(parsed.from)) {
          errors.push({
            line: lineNum,
            lineContent: line,
            message: `Unknown step ID: ${parsed.from}`,
            friendlyMessage: `The step "@${parsed.from}" hasn't been defined yet.`,
            suggestion: `Add @${parsed.from} to one of your pattern steps, like: "Section 2 @${parsed.from}"`,
          });
        }
        if (!stepIds.includes(parsed.to)) {
          errors.push({
            line: lineNum,
            lineContent: line,
            message: `Unknown step ID: ${parsed.to}`,
            friendlyMessage: `The step "@${parsed.to}" hasn't been defined yet.`,
            suggestion: `Add @${parsed.to} to one of your pattern steps, like: "Section 2 @${parsed.to}"`,
          });
        }
        // For count measurements, validate countOf step exists and has quantifier
        if (parsed.type === "count" && parsed.countOf) {
          if (!stepIds.includes(parsed.countOf)) {
            errors.push({
              line: lineNum,
              lineContent: line,
              message: `Unknown step ID: ${parsed.countOf}`,
              friendlyMessage: `The step "@${parsed.countOf}" to count hasn't been defined yet.`,
              suggestion: `Add @${parsed.countOf} to one of your pattern steps with a quantifier, like: "Section 17A (Recalled) + @${parsed.countOf}"`,
            });
          } else {
            // Check that the step has a quantifier (+ or *)
            const quantifier = stepQuantifiers.get(parsed.countOf);
            if (quantifier === "one" || quantifier === "zeroOrOne") {
              errors.push({
                line: lineNum,
                lineContent: line,
                message: `Step "${parsed.countOf}" must have a + or * quantifier for counting`,
                friendlyMessage: `To count occurrences of @${parsed.countOf}, it needs a + or * quantifier.`,
                suggestion: `Change the step to use a quantifier, like: "Section 17A (Recalled) + @${parsed.countOf}" or "* @${parsed.countOf}"`,
              });
            }
          }
        }
      }
      continue;
    }

    // Parse as step
    hasSteps = true;

    // Anchor position checks: ^ only on the first step, $ only on the last.
    if (line.startsWith("^") && lineNum !== firstStepLineNum) {
      errors.push({
        line: lineNum,
        lineContent: line,
        message: "Misplaced ^ anchor",
        friendlyMessage: "The ^ start-anchor is only valid on the first step.",
        suggestion: "Move ^ to the first step, or remove it.",
        highlight: "^",
      });
      continue;
    }
    if (line.endsWith("$") && lineNum !== lastStepLineNum) {
      errors.push({
        line: lineNum,
        lineContent: line,
        message: "Misplaced $ anchor",
        friendlyMessage: "The $ end-anchor is only valid on the last step.",
        suggestion: "Move $ to the last step, or remove it.",
        highlight: "$",
      });
      continue;
    }

    const stepError = validateStepLine(line, lineNum);
    if (stepError) {
      errors.push(stepError);
    } else {
      // Extract step ID and quantifier if present
      const idMatch = line.match(/@(\w+)/);
      if (idMatch) {
        const stepId = group(idMatch, 1);
        stepIds.push(stepId);
        const step = parseStepLine(
          line
            .replace(/^\^\s*/, "")
            .replace(/\s*\$$/, "")
            .trim(),
        );
        if (step) {
          stepQuantifiers.set(stepId, step.quantifier);
        }
      }
    }
  }

  // Check for empty pattern
  if (!hasSteps && errors.length === 0) {
    errors.push({
      line: 0,
      lineContent: "",
      message: "No pattern steps defined",
      friendlyMessage: "Your pattern doesn't have any steps to match.",
      suggestion:
        'Add steps after "---". Example:\n---\nSection 2 >= 7d\nNot Detained\nSection 2',
    });
  }

  // Try to parse and catch any other errors
  let pattern: PathwayPattern | null = null;
  if (errors.length === 0) {
    try {
      pattern = parsePathwayDsl(dsl);
    } catch (e) {
      errors.push({
        line: 0,
        lineContent: "",
        message: e instanceof Error ? e.message : String(e),
        friendlyMessage: "There's an issue with the pattern structure.",
        suggestion: undefined,
      });
    }
  }

  return {
    valid: errors.length === 0,
    errors,
    pattern,
  };
}

function validateStepLine(
  line: string,
  lineNum: number,
): DslValidationError | null {
  let workingLine = line;

  // Strip episode anchors (^ start / $ end); their POSITION is validated in
  // validatePathwayDsl. Remove them here so status parsing isn't confused.
  workingLine = workingLine
    .replace(/^\^\s*/, "")
    .replace(/\s*\$$/, "")
    .trim();

  // Remove @id
  workingLine = workingLine.replace(/@\w+/, "").trim();

  // Check for invalid quantifiers BEFORE removing them
  // Valid quantifiers at end: ?, ??, *, *?, +, +?
  // Invalid: **, ***, ++, +++, ???, or combinations like **?, *+, etc.
  const invalidQuantifierPatterns = [
    /\s+\*{2,}\??\s*$/, // ** or *** or **? etc
    /\s+\+{2,}\??\s*$/, // ++ or +++ or ++? etc
    /\s+\?{3,}\s*$/, // ??? or more
    /\s+[*+][*+]+\??\s*$/, // mixed like *+ or +* etc
  ];
  for (const pattern of invalidQuantifierPatterns) {
    const match = workingLine.match(pattern);
    if (match) {
      const badQuantifier = match[0].trim();
      let suggestion: string | undefined;
      if (badQuantifier.startsWith("*")) {
        suggestion =
          'Use "*" for zero-or-more, or "*?" for zero-or-more non-greedy';
      } else if (badQuantifier.startsWith("+")) {
        suggestion =
          'Use "+" for one-or-more, or "+?" for one-or-more non-greedy';
      } else if (badQuantifier.startsWith("?")) {
        suggestion = 'Use "?" for optional, or "??" for optional non-greedy';
      }
      return {
        line: lineNum,
        lineContent: line,
        message: `Invalid quantifier: ${badQuantifier}`,
        friendlyMessage: `"${badQuantifier}" isn't a valid quantifier.`,
        suggestion,
        highlight: badQuantifier,
      };
    }
  }

  // Check for invalid duration operators BEFORE removing durations
  // Invalid: <<, >>, <<=, >>=, <<<, etc.
  const invalidOperatorMatch = workingLine.match(/([<>]{2,}=?)\s*\d*d?/);
  if (invalidOperatorMatch) {
    const badOp = group(invalidOperatorMatch, 1);
    let suggestion: string | undefined;
    if (badOp.startsWith("<<")) {
      suggestion = 'Use "<" for "less than" or "<=" for "less than or equal"';
    } else if (badOp.startsWith(">>")) {
      suggestion =
        'Use ">" for "greater than" or ">=" for "greater than or equal"';
    }
    return {
      line: lineNum,
      lineContent: line,
      message: `Invalid operator: ${badOp}`,
      friendlyMessage: `"${badOp}" isn't a valid duration operator.`,
      suggestion,
      highlight: badOp,
    };
  }

  // Check for standalone = operator (should be >= or <=)
  const standaloneEqualsMatch = workingLine.match(/((?<![<>])=\s*\d+d)/);
  if (standaloneEqualsMatch) {
    return {
      line: lineNum,
      lineContent: line,
      message: "Invalid operator: =",
      friendlyMessage: '"=" isn\'t a valid duration operator on its own.',
      suggestion:
        'Use ">=" for "at least", "<=" for "at most", or just the number like "7d" for exact duration',
      highlight: standaloneEqualsMatch[1],
    };
  }

  // Remove valid quantifiers
  workingLine = workingLine
    .replace(/\s*\*\?\s*$/, "")
    .replace(/\s*\+\?\s*$/, "")
    .replace(/\s*\?\?\s*$/, "")
    .replace(/\s*\*\s*$/, "")
    .replace(/\s*\+\s*$/, "")
    .replace(/\s*\?\s*$/, "")
    .replace(/\s*~\s*/, " ")
    .trim();

  // Remove duration constraints (order matters - check longer patterns first)
  workingLine = workingLine
    .replace(/>=\s*\d+d\s*<=\s*\d+d/, "")
    .replace(/>\s*\d+d\s*<\s*\d+d/, "")
    .replace(/>=\s*\d+d/, "")
    .replace(/>\s*\d+d/, "")
    .replace(/<=\s*\d+d/, "")
    .replace(/<\s*\d+d/, "")
    .replace(/\d+d\s*-\s*\d+d/, "")
    .replace(/\d+d/, "")
    .trim();

  // Check for wildcard patterns (valid)
  if (workingLine === "*") return null;

  // A step must have a status. If stripping the @id, quantifier and duration
  // left nothing (e.g. a bare "*?", "+" or "7d"), it's not a valid step — flag
  // it rather than letting parseStepLine silently drop it (PR review: Copilot).
  if (workingLine === "") {
    return {
      line: lineNum,
      lineContent: line,
      message: "Step has no status",
      friendlyMessage:
        "This step has a quantifier or duration but no detention status.",
      suggestion:
        'Add a status before the quantifier, e.g. "Section 2 +", or use "*" for any status.',
    };
  }

  // Check for exclusion patterns: "* !Section 2" or implicit "!Section 2"
  // Supports both | and , as separators
  const hasExplicitWildcard =
    workingLine.startsWith("*") && workingLine.includes("!");
  const hasImplicitExclusion =
    workingLine.startsWith("!") && !workingLine.startsWith("*");

  if (hasExplicitWildcard || hasImplicitExclusion) {
    const excludeText = hasExplicitWildcard
      ? workingLine.slice(1).trim()
      : workingLine;
    // Split by | or , (with optional whitespace)
    const parts = excludeText
      .split(/\s*[|,]\s*/)
      .map((s) => s.trim())
      .filter(Boolean);

    // Check if all parts are exclusions
    const allExclusions = parts.every((p) => p.startsWith("!"));
    if (!allExclusions) {
      return {
        line: lineNum,
        lineContent: line,
        message: "Mixed positive and negative status matches",
        friendlyMessage:
          "Can't mix included and excluded statuses in the same step.",
        suggestion: "Use either all positive (2 | 3) or all negative (!2, !3)",
      };
    }

    // Validate each excluded status
    for (const part of parts) {
      const excl = part.replace(/^!/, "").trim();
      const suggestion = suggestStatus(excl);
      if (suggestion && suggestion !== excl) {
        return {
          line: lineNum,
          lineContent: line,
          message: `Unknown status: ${excl}`,
          friendlyMessage: `"${excl}" isn't a recognised detention status.`,
          suggestion: `Did you mean "${suggestion}"?`,
          highlight: excl,
        };
      }
    }
    return null;
  }

  // Check for "one of" patterns (supports both | and ,)
  if (workingLine.includes("|") || workingLine.includes(",")) {
    const statuses = workingLine.split(/\s*[|,]\s*/).map((s) => s.trim());
    for (const status of statuses) {
      const suggestion = suggestStatus(status);
      if (suggestion && suggestion !== status) {
        return {
          line: lineNum,
          lineContent: line,
          message: `Unknown status: ${status}`,
          friendlyMessage: `"${status}" isn't a recognised detention status.`,
          suggestion: `Did you mean "${suggestion}"?`,
          highlight: status,
        };
      }
    }
    return null;
  }

  // Check exact status match
  if (workingLine) {
    const suggestion = suggestStatus(workingLine);
    if (suggestion && suggestion !== workingLine) {
      return {
        line: lineNum,
        lineContent: line,
        message: `Unknown status: ${workingLine}`,
        friendlyMessage: `"${workingLine}" isn't a recognised detention status.`,
        suggestion: `Did you mean "${suggestion}"?`,
        highlight: workingLine,
      };
    }
  }

  return null;
}

function suggestStatus(input: string): string | null {
  const normalised = input.toLowerCase().trim();

  // Common typos and variations
  // Suggestions for typos/variations that aren't in VALID_STATUSES
  // Note: s2, s3, 2, 3, ND, CTO-R etc. are now valid and don't need suggestions
  const suggestions: Record<string, string> = {
    // Typos with spaces
    "sec 2": "Section 2",
    "sec 3": "Section 3",
    "sec 4": "Section 4",
    // Typos without spaces
    sec2: "Section 2",
    sec3: "Section 3",
    sec4: "Section 4",
    section2: "Section 2",
    section3: "Section 3",
    section4: "Section 4",
    section17a: "Section 17A",
    "section 17": "Section 17A",
    // Ambiguous s5 → default to 5(2)
    s5: "Section 5(2)",
    S5: "Section 5(2)",
    "5": "Section 5(2)",
    // Section 136 typos
    "sec 136": "Section 136",
    sec136: "Section 136",
    section136: "Section 136",
    police: "Section 136",
    // Common descriptions
    "community treatment order": "CTO",
    "not detained": "Not Detained",
    notdetained: "Not Detained",
    informal: "Not Detained",
    discharged: "Not Detained",
    discharge: "Not Detained",
    // CTO recalled variations
    "cto recalled": "CTO (Recalled)",
    "cto (recall)": "CTO (Recalled)",
    "cto recall": "CTO (Recalled)",
    recalled: "CTO (Recalled)",
    "section 17a recalled": "CTO (Recalled)",
    "section 17a (recall)": "CTO (Recalled)",
    "17a recalled": "CTO-R",
    "17a-recalled": "CTO-R",
  };

  // Direct lookup
  if (suggestions[normalised]) {
    return suggestions[normalised];
  }

  // Already a valid status in one of its listed casings — return the input
  // unchanged. Re-casing it to the first-listed variant here would make
  // validateStepLine (which errors whenever the suggestion differs from the
  // input) falsely reject valid shorthands like "S2" or "nd".
  const validLower = VALID_STATUSES.map((s) => s.toLowerCase());
  if (validLower.includes(normalised)) {
    return input;
  }

  // Fuzzy matching - find closest valid status
  let bestMatch: string | null = null;
  let bestScore = 0;

  for (const valid of VALID_STATUSES) {
    const score = similarityScore(normalised, valid.toLowerCase());
    if (score > bestScore && score > 0.5) {
      bestScore = score;
      bestMatch = valid;
    }
  }

  return bestMatch;
}

function similarityScore(a: string, b: string): number {
  // Simple Jaccard-like similarity on character bigrams
  const bigramsA: string[] = [];
  const bigramsB: string[] = [];

  for (let i = 0; i < a.length - 1; i++) {
    bigramsA.push(a.slice(i, i + 2));
  }
  for (let i = 0; i < b.length - 1; i++) {
    bigramsB.push(b.slice(i, i + 2));
  }

  const setB = new Set(bigramsB);
  let intersection = 0;
  for (const bg of bigramsA) {
    if (setB.has(bg)) intersection++;
  }

  const union = new Set([...bigramsA, ...bigramsB]).size;
  return union === 0 ? 0 : intersection / union;
}

// ============================================================================
// String DSL Parser
// ============================================================================

/**
 * Syntax:
 *   Section 2 >= 7d                    # exact match, min 7 days
 *   Section 2 <= 14d                   # max 14 days
 *   Section 2 >= 7d <= 28d             # min 7 days, max 28 days
 *   Section 2 7d-14d                   # 7-14 days range (alternative)
 *   * !Not Detained                    # any except
 *   * !Section 2, !Section 3           # any except multiple
 *   *                                  # any status
 *   Section 2 | Section 3              # one of
 *   Section 2 >= 7d  @release          # with id for measurements
 *
 * Quantifiers (regex-style):
 *   Section 2 >= 7d                    # exactly one (default)
 *   Section 2 >= 7d  ?                 # 0 or 1 (optional), greedy
 *   Section 2 >= 7d  ??                # 0 or 1, non-greedy
 *   Section 2 >= 7d  +                 # 1 or more, greedy
 *   Section 2 >= 7d  +?                # 1 or more, non-greedy
 *   * !Not Detained  *                 # 0 or more, greedy
 *   * !Not Detained  *?                # 0 or more, non-greedy
 *
 *   measure: release -> readmit
 *   measure: release.end -> readmit.start
 */
export function parsePathwayDsl(dsl: string): PathwayPattern {
  const lines = dsl
    .split("\n")
    .map((l) => l.trim())
    .filter((l) => l && !l.startsWith("#"));

  let name = "Unnamed Pattern";
  const stepLines: string[] = [];
  const measurements: Measurement[] = [];
  let section: "header" | "steps" | "measures" = "header";

  for (const line of lines) {
    if (line === "---") {
      section = section === "header" ? "steps" : "measures";
      continue;
    }
    if (section === "header") {
      name = line;
      continue;
    }
    if (
      section === "measures" ||
      line.startsWith("measure:") ||
      line.startsWith("count:") ||
      line.startsWith("count ")
    ) {
      const m = parseMeasurementLine(line);
      if (m) measurements.push(m);
      continue;
    }
    stepLines.push(line);
  }

  // Episode anchors (regex-style): ^ on the first step line pins the match to
  // the start of the input period sequence; $ on the last pins it to the end.
  // The scope is whatever sequence the matcher is given (an episode, or the
  // whole timeline), so ^…$ over one episode means "this episode is exactly …".
  let anchorStart = false;
  let anchorEnd = false;
  const firstStepLine = stepLines[0];
  if (firstStepLine?.startsWith("^")) {
    anchorStart = true;
    stepLines[0] = firstStepLine.slice(1).trim();
  }
  const lastIdx = stepLines.length - 1;
  const lastStepLine = stepLines[lastIdx];
  if (lastStepLine?.endsWith("$")) {
    anchorEnd = true;
    stepLines[lastIdx] = lastStepLine.slice(0, -1).trim();
  }

  const steps: PatternStep[] = [];
  for (const stepLine of stepLines) {
    const step = parseStepLine(stepLine);
    if (step) steps.push(step);
  }

  return { name, steps, measurements, anchorStart, anchorEnd };
}

/**
 * The `.start` / `.end` suffix on a step reference. The regex captures any word,
 * so an unrecognised suffix is treated as absent and takes the caller's default.
 */
function edge(
  captured: string | undefined,
  fallback: "start" | "end",
): "start" | "end" {
  return captured === "start" || captured === "end" ? captured : fallback;
}

function parseMeasurementLine(line: string): Measurement | null {
  // Remove prefix keywords and detect type
  const isCount = line.startsWith("count:") || line.startsWith("count ");
  const content = line
    .replace(/^measure:\s*/, "")
    .replace(/^count:\s*/, "")
    .replace(/^count\s+/, "")
    .trim();

  if (isCount) {
    // Count syntax variants:
    // "Name": target in from -> to
    // "Name": target between from, to
    // Name: target @ from -> to
    const countPatterns = [
      // "Name": target in from[.edge] -> to[.edge]
      /^(?:"([^"]+)":\s*)?(\w+)\s+in\s+(\w+)(?:\.(\w+))?\s*->\s*(\w+)(?:\.(\w+))?$/,
      // "Name": target between from[.edge], to[.edge]
      /^(?:"([^"]+)":\s*)?(\w+)\s+between\s+(\w+)(?:\.(\w+))?\s*,\s*(\w+)(?:\.(\w+))?$/,
      // "Name": target @ from[.edge] -> to[.edge]
      /^(?:"([^"]+)":\s*)?(\w+)\s*@\s*(\w+)(?:\.(\w+))?\s*->\s*(\w+)(?:\.(\w+))?$/,
      // Name: target in from -> to (unquoted name with colon)
      /^([^:]+):\s*(\w+)\s+in\s+(\w+)(?:\.(\w+))?\s*->\s*(\w+)(?:\.(\w+))?$/,
    ];

    for (const pattern of countPatterns) {
      const match = content.match(pattern);
      if (match) {
        const measureName = match[1];
        const countOf = group(match, 2);
        return {
          name: measureName?.trim() || `${countOf} count`,
          type: "count",
          countOf,
          from: group(match, 3),
          to: group(match, 5),
          fromEdge: edge(match[4], "start"),
          toEdge: edge(match[6], "end"),
        };
      }
    }
    return null;
  }

  // Duration measurement syntax: ["name":] [name:] from[.edge] -> to[.edge]
  const durationPatterns = [
    // "Name": from[.edge] -> to[.edge]
    /^"([^"]+)":\s*(\w+)(?:\.(\w+))?\s*->\s*(\w+)(?:\.(\w+))?$/,
    // Name: from[.edge] -> to[.edge]
    /^([^:]+):\s*(\w+)(?:\.(\w+))?\s*->\s*(\w+)(?:\.(\w+))?$/,
    // from[.edge] -> to[.edge] (no name)
    /^(\w+)(?:\.(\w+))?\s*->\s*(\w+)(?:\.(\w+))?$/,
  ];

  for (const pattern of durationPatterns) {
    const match = content.match(pattern);
    if (match) {
      if (match.length === 5) {
        // No name pattern
        const from = group(match, 1);
        const to = group(match, 3);
        return {
          name: `${from} to ${to}`,
          type: "duration",
          from,
          to,
          fromEdge: edge(match[2], "start"),
          toEdge: edge(match[4], "start"),
        };
      } else {
        const measureName = match[1];
        const from = group(match, 2);
        const to = group(match, 4);
        return {
          name: measureName?.trim() || `${from} to ${to}`,
          type: "duration",
          from,
          to,
          fromEdge: edge(match[3], "start"),
          toEdge: edge(match[5], "start"),
        };
      }
    }
  }

  return null;
}

function parseStepLine(line: string): PatternStep | null {
  let id: string | undefined;
  let quantifier: PatternStep["quantifier"] = "one";
  let greedy = true;

  // Extract @id
  const idMatch = line.match(/@(\w+)/);
  if (idMatch) {
    id = idMatch[1];
    line = line.replace(/@\w+/, "").trim();
  }

  // Parse quantifiers (regex-style) - order matters, check longer patterns first
  // *? = zero or more, non-greedy
  // +? = one or more, non-greedy
  // ?? = zero or one, non-greedy
  // * = zero or more (at end of line, not start which is wildcard)
  // + = one or more
  // ? = zero or one
  // ~ = legacy non-greedy modifier

  if (/\s\*\?$/.test(line) || line === "*?") {
    quantifier = "zeroOrMore";
    greedy = false;
    line = line.replace(/\s*\*\?\s*$/, "").trim();
  } else if (/\s\+\?$/.test(line) || line.endsWith("+?")) {
    quantifier = "oneOrMore";
    greedy = false;
    line = line.replace(/\s*\+\?\s*$/, "").trim();
  } else if (/\s\?\?$/.test(line) || line.endsWith("??")) {
    quantifier = "zeroOrOne";
    greedy = false;
    line = line.replace(/\s*\?\?\s*$/, "").trim();
  } else if (/\s\*$/.test(line)) {
    // Only match * at end with whitespace before (not the wildcard * at start)
    quantifier = "zeroOrMore";
    line = line.replace(/\s*\*\s*$/, "").trim();
  } else if (/\s\+$/.test(line) || line.endsWith("+")) {
    quantifier = "oneOrMore";
    line = line.replace(/\s*\+\s*$/, "").trim();
  } else if (/\s\?$/.test(line) || line.endsWith("?")) {
    quantifier = "zeroOrOne";
    line = line.replace(/\s*\?\s*$/, "").trim();
  } else if (line.includes(" ~") || line.endsWith("~")) {
    // Legacy: ~ for non-greedy (with default "one" quantifier)
    greedy = false;
    line = line.replace(/\s*~\s*/, " ").trim();
  }

  let duration: PatternStep["duration"];
  // Check for combined >= min <= max syntax first (e.g., >= 7d <= 28d or > 7d < 28d)
  const combinedMatch = line.match(/(>=?)\s*(\d+)d\s*(<=?)\s*(\d+)d/);
  if (combinedMatch) {
    duration = {
      min: parseInt(group(combinedMatch, 2)),
      max: parseInt(group(combinedMatch, 4)),
      minExclusive: group(combinedMatch, 1) === ">",
      maxExclusive: group(combinedMatch, 3) === "<",
    };
    line = line.replace(/(>=?)\s*\d+d\s*(<=?)\s*\d+d/, "").trim();
  } else {
    // Order matters - check longer patterns first to avoid partial matches
    const patterns: Array<{
      regex: RegExp;
      handler: (m: RegExpMatchArray) => PatternStep["duration"];
    }> = [
      {
        regex: />=\s*(\d+)d/,
        handler: (m) => ({ min: parseInt(group(m, 1)) }),
      },
      {
        regex: />\s*(\d+)d/,
        handler: (m) => ({ min: parseInt(group(m, 1)), minExclusive: true }),
      },
      {
        regex: /<=\s*(\d+)d/,
        handler: (m) => ({ max: parseInt(group(m, 1)) }),
      },
      {
        regex: /<\s*(\d+)d/,
        handler: (m) => ({ max: parseInt(group(m, 1)), maxExclusive: true }),
      },
      {
        regex: /(\d+)d\s*-\s*(\d+)d/,
        handler: (m) => ({
          min: parseInt(group(m, 1)),
          max: parseInt(group(m, 2)),
        }),
      },
      { regex: /(\d+)d/, handler: (m) => ({ min: parseInt(group(m, 1)) }) },
    ];
    for (const { regex, handler } of patterns) {
      const m = line.match(regex);
      if (m) {
        duration = handler(m);
        line = line.replace(regex, "").trim();
        break;
      }
    }
  }

  const match = parseStatusMatch(line);
  if (!match) return null;
  return { id, match, duration, quantifier, greedy };
}

function parseStatusMatch(text: string): StatusMatch | null {
  text = text.trim();

  // Check for exclusion patterns: "* !Section 2" or implicit "!Section 2"
  // Supports both | and , as separators: "!2, !3" or "!2 | !3"
  const hasExplicitWildcard = text.startsWith("*") && text.includes("!");
  const hasImplicitExclusion = text.startsWith("!") && !text.startsWith("*");

  if (hasExplicitWildcard || hasImplicitExclusion) {
    const excludeText = hasExplicitWildcard ? text.slice(1).trim() : text;
    // Split by | or , (with optional whitespace)
    const parts = excludeText
      .split(/\s*[|,]\s*/)
      .map((s) => s.trim())
      .filter(Boolean);

    // All parts must be exclusions (start with !)
    if (parts.every((p) => p.startsWith("!"))) {
      const excludes = parts.map((s) =>
        expandStatusAlias(s.replace(/^!/, "").trim()),
      );
      return { type: "anyExcept", exclude: excludes };
    }
    // If mixed positive/negative, fall through to error or oneOf handling
  }

  if (text === "*") return { type: "any" };

  // Support both | and , as OR separators for oneOf
  if (text.includes("|") || text.includes(",")) {
    const statuses = text
      .split(/\s*[|,]\s*/)
      .map((s) => expandStatusAlias(s.trim()));
    return { type: "oneOf", statuses };
  }

  if (text) return { type: "exact", status: expandStatusAlias(text) };
  return null;
}

// ============================================================================
// Matcher Engine
// ============================================================================

// Normalise section for consolidation - "Expired" is treated as "Not Detained"
function normaliseSectionForConsolidation(section: string): string {
  return section === "Expired" ? "Not Detained" : section;
}

// Status aliases - shorthand names that map to canonical status names
const STATUS_ALIASES: Record<string, string> = {
  // CTO aliases
  CTO: "Section 17A",
  "CTO (Recalled)": "Section 17A (Recalled)",
  "CTO-R": "Section 17A (Recalled)",
  // Numeric shorthand
  "2": "Section 2",
  "3": "Section 3",
  "4": "Section 4",
  "5(2)": "Section 5(2)",
  "5(4)": "Section 5(4)",
  "17A": "Section 17A",
  "17A-R": "Section 17A (Recalled)",
  "136": "Section 136",
  // s-prefixed shorthand (lowercase)
  s2: "Section 2",
  s3: "Section 3",
  s4: "Section 4",
  "s5(2)": "Section 5(2)",
  "s5(4)": "Section 5(4)",
  s17a: "Section 17A",
  "s17a-r": "Section 17A (Recalled)",
  s136: "Section 136",
  // S-prefixed shorthand (uppercase)
  S2: "Section 2",
  S3: "Section 3",
  S4: "Section 4",
  "S5(2)": "Section 5(2)",
  "S5(4)": "Section 5(4)",
  S17A: "Section 17A",
  "S17A-R": "Section 17A (Recalled)",
  S136: "Section 136",
  // Not Detained shorthand
  ND: "Not Detained",
  nd: "Not Detained",
  // Deceased aliases
  deceased: "Deceased",
  dead: "Deceased",
  died: "Deceased",
  death: "Deceased",
};

/**
 * Expand a pattern status to its canonical form.
 * e.g., "CTO" -> "Section 17A"
 */
function expandStatusAlias(status: string): string {
  return STATUS_ALIASES[status] ?? status;
}

function periodMatchesStatus(
  period: DetentionPeriod,
  match: StatusMatch,
): boolean {
  // Periods are already normalised during consolidation, but apply again for safety
  const section = normaliseSectionForConsolidation(period.section);
  switch (match.type) {
    case "exact":
      return section === expandStatusAlias(match.status);
    case "any":
      return true;
    case "anyExcept":
      return !match.exclude
        .map((s) => expandStatusAlias(normaliseSectionForConsolidation(s)))
        .includes(section);
    case "oneOf":
      return match.statuses
        .map((s) => expandStatusAlias(normaliseSectionForConsolidation(s)))
        .includes(section);
  }
}

function daysBetween(start: Date, end: Date): number {
  return Math.floor((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
}

/**
 * Check if a duration in days satisfies the step's duration constraints.
 * Supports both inclusive (>= <=) and exclusive (> <) bounds.
 */
function checkDuration(
  days: number,
  duration: PatternStep["duration"],
): boolean {
  if (!duration) return true;

  // Check minimum
  if (duration.min !== undefined) {
    if (duration.minExclusive) {
      if (days <= duration.min) return false;
    } else {
      if (days < duration.min) return false;
    }
  }

  // Check maximum
  if (duration.max !== undefined) {
    if (duration.maxExclusive) {
      if (days >= duration.max) return false;
    } else {
      if (days > duration.max) return false;
    }
  }

  return true;
}

/**
 * Calculate measurements for a pathway match.
 * Supports both duration measurements (days between steps) and count measurements
 * (count of periods matching a step ID between two date boundaries).
 */
function calculateMeasurements(
  measurements: Measurement[],
  stepMatches: StepMatch[],
): MeasurementResult[] {
  const results: MeasurementResult[] = [];

  for (const m of measurements) {
    const fromStep = stepMatches.find((s) => s.stepId === m.from);
    const toStep = stepMatches.find((s) => s.stepId === m.to);
    if (!fromStep || !toStep) continue;

    const fromDate =
      m.fromEdge === "end" ? fromStep.endDate : fromStep.startDate;
    const toDate = m.toEdge === "end" ? toStep.endDate : toStep.startDate;
    // Status at the END of the measured span — taken from the `to` step, not
    // `from`. For a spanning measure (from ≠ to) the `from` step has already
    // transitioned and is never "active", so deriving endStatus from it would
    // let still-open (asOf-capped) or auto-expired episodes leak into the
    // end-anchored / excludeExpired views in metrics.ts. (For the common
    // from === to entry-cohort measure this is unchanged.)
    const endStatus =
      toStep.periods[toStep.periods.length - 1]?.completionStatus;

    if (m.type === "count" && m.countOf) {
      // Count occurrences of countOf step between from and to dates
      // Exclude exact boundary matches (strictly between)
      const countStep = stepMatches.find((s) => s.stepId === m.countOf);
      let count = 0;
      if (countStep) {
        count = countStep.periods.filter((p) => {
          const periodStart = p.startDate.getTime();
          // Strictly between: > fromDate and < toDate
          return (
            periodStart > fromDate.getTime() && periodStart < toDate.getTime()
          );
        }).length;
      }
      results.push({
        name: m.name,
        type: "count",
        value: count,
        days: count, // backwards compatibility
        fromDate,
        toDate,
        endStatus,
      });
    } else {
      // Duration measurement
      const days = daysBetween(fromDate, toDate);
      results.push({
        name: m.name,
        type: "duration",
        value: days,
        days, // backwards compatibility
        fromDate,
        toDate,
        endStatus,
      });
    }
  }

  return results;
}

export function matchPathway(
  periods: DetentionPeriod[],
  pattern: PathwayPattern,
): PathwayMatch | null {
  const result = matchStepsRecursive(
    periods,
    0,
    pattern.steps,
    0,
    [],
    pattern.anchorEnd ?? false,
  );
  if (!result) return null;

  const measurements = calculateMeasurements(
    pattern.measurements || [],
    result,
  );

  return { matched: true, stepMatches: result, measurements };
}

/**
 * Create a unique key for a match based on the periods from named steps (those with @id).
 * This prevents duplicate counting when wildcards consume different amounts.
 */
function getMatchKey(stepMatches: StepMatch[]): string {
  // Only include periods from steps that have an ID (user explicitly named them)
  // If no IDs, fall back to all periods
  const namedSteps = stepMatches.filter((sm) => sm.stepId);
  const stepsToUse = namedSteps.length > 0 ? namedSteps : stepMatches;

  return stepsToUse
    .flatMap((sm) => sm.periods)
    .map(
      (p) =>
        `${p.section}:${String(p.startDate.getTime())}:${String(p.endDate.getTime())}`,
    )
    .join("|");
}

/**
 * Find all matches of the pattern in the patient's periods (capturing multiple
 * readmissions, conversions, etc. for the same patient). The scan advances one
 * period at a time, so matches CAN overlap; duplicates that cover the exact
 * same meaningful periods are removed via the match key.
 */
export function matchAllPathways(
  periods: DetentionPeriod[],
  pattern: PathwayPattern,
): PathwayMatch[] {
  const allMatches: PathwayMatch[] = [];
  const seenKeys = new Set<string>();
  let startIndex = 0;
  const maxIterations = periods.length * 2; // Safety limit
  let iterations = 0;

  while (startIndex < periods.length && iterations < maxIterations) {
    iterations++;
    const result = matchStepsRecursive(
      periods,
      startIndex,
      pattern.steps,
      0,
      [],
      pattern.anchorEnd ?? false,
    );

    if (result && result.length > 0) {
      // Deduplicate based on named steps (@id) to avoid counting same match multiple times
      const matchKey = getMatchKey(result);
      if (!seenKeys.has(matchKey)) {
        seenKeys.add(matchKey);

        // Calculate measurements for this match
        const measurements = calculateMeasurements(
          pattern.measurements || [],
          result,
        );

        allMatches.push({ matched: true, stepMatches: result, measurements });
      }

      // Always advance past current startIndex to prevent infinite loop
      // The match was found starting from startIndex, so move to startIndex + 1
      startIndex++;
    } else {
      startIndex++;
    }

    // A start-anchor (^) fixes the match to index 0, so don't scan further.
    if (pattern.anchorStart) break;
  }

  return allMatches;
}

function matchStepsRecursive(
  periods: DetentionPeriod[],
  periodIndex: number,
  steps: PatternStep[],
  stepIndex: number,
  currentMatches: StepMatch[],
  anchorEnd: boolean,
): StepMatch[] | null {
  const step = steps[stepIndex];
  if (step === undefined) {
    // Ran off the end of the pattern — every step matched. With an end-anchor
    // ($), the whole pattern must also consume through to the final period of
    // the input sequence.
    if (anchorEnd && periodIndex !== periods.length) return null;
    return currentMatches;
  }
  const { quantifier, greedy } = step;

  // For "one" quantifier - match exactly one period at the current position
  // No scanning ahead - if user wants to skip periods, they must use explicit wildcards
  if (quantifier === "one") {
    const period = periods[periodIndex];
    if (period === undefined) return null;
    if (!periodMatchesStatus(period, step.match)) return null;

    const days = daysBetween(period.startDate, period.endDate);
    if (!checkDuration(days, step.duration)) return null;

    const stepMatch: StepMatch = {
      stepId: step.id,
      periods: [period],
      startDate: period.startDate,
      endDate: period.endDate,
    };
    return matchStepsRecursive(
      periods,
      periodIndex + 1,
      steps,
      stepIndex + 1,
      [...currentMatches, stepMatch],
      anchorEnd,
    );
  }

  // For "zeroOrOne" (?) - POSITIONAL: either match exactly the period at the
  // cursor, or match zero and leave the cursor where it is. No scanning ahead
  // (consistent with "one" above and the +/* branch below) — skipping over
  // non-matching periods is what explicit wildcards are for. Walking forward to
  // find the optional period would make "?" behave like ".*?" and produce
  // false-positive matches (PR review: chrissyPocock). Greedy prefers the
  // match, non-greedy prefers the skip; both backtrack on overall failure.
  if (quantifier === "zeroOrOne") {
    const matchHere = (): StepMatch[] | null => {
      const period = periods[periodIndex];
      if (period === undefined) return null;
      if (!periodMatchesStatus(period, step.match)) return null;
      const days = daysBetween(period.startDate, period.endDate);
      if (!checkDuration(days, step.duration)) return null;
      const stepMatch: StepMatch = {
        stepId: step.id,
        periods: [period],
        startDate: period.startDate,
        endDate: period.endDate,
      };
      return matchStepsRecursive(
        periods,
        periodIndex + 1,
        steps,
        stepIndex + 1,
        [...currentMatches, stepMatch],
        anchorEnd,
      );
    };
    const skip = (): StepMatch[] | null =>
      matchStepsRecursive(
        periods,
        periodIndex,
        steps,
        stepIndex + 1,
        currentMatches,
        anchorEnd,
      );

    if (greedy) return matchHere() ?? skip();
    return skip() ?? matchHere();
  }

  // For "oneOrMore" (+) and "zeroOrMore" (*) - match consecutive periods
  const minMatches = quantifier === "zeroOrMore" ? 0 : 1;

  // Collect all possible consecutive match spans
  const possibleSpans: {
    start: number;
    end: number;
    matched: DetentionPeriod[];
  }[] = [];

  for (let start = periodIndex; start < periods.length; start++) {
    const consecutiveMatches: DetentionPeriod[] = [];

    for (const period of periods.slice(start)) {
      if (!periodMatchesStatus(period, step.match)) break;

      const days = daysBetween(period.startDate, period.endDate);
      if (!checkDuration(days, step.duration)) break;

      consecutiveMatches.push(period);

      if (consecutiveMatches.length >= minMatches) {
        possibleSpans.push({
          start,
          end: start + consecutiveMatches.length - 1,
          matched: [...consecutiveMatches],
        });
      }
    }

    // Only try starting from the first position for consecutive matching
    break;
  }

  // For zero-or-more, also add the "match nothing" option
  if (quantifier === "zeroOrMore") {
    possibleSpans.push({
      start: periodIndex,
      end: periodIndex - 1,
      matched: [],
    });
  }

  // Sort by greedy preference
  if (greedy) {
    possibleSpans.sort((a, b) => b.matched.length - a.matched.length);
  } else {
    possibleSpans.sort((a, b) => a.matched.length - b.matched.length);
  }

  for (const span of possibleSpans) {
    const firstMatched = span.matched[0];
    const lastMatched = span.matched.at(-1);
    if (firstMatched === undefined || lastMatched === undefined) {
      // Skip this step (zero matches)
      const result = matchStepsRecursive(
        periods,
        periodIndex,
        steps,
        stepIndex + 1,
        currentMatches,
        anchorEnd,
      );
      if (result) return result;
    } else {
      const stepMatch: StepMatch = {
        stepId: step.id,
        periods: span.matched,
        startDate: firstMatched.startDate,
        endDate: lastMatched.endDate,
      };
      const result = matchStepsRecursive(
        periods,
        span.end + 1,
        steps,
        stepIndex + 1,
        [...currentMatches, stepMatch],
        anchorEnd,
      );
      if (result) return result;
    }
  }

  return null;
}

// ============================================================================
// Predefined Patterns
// ============================================================================

export const PREDEFINED_PATTERNS = {
  section2Readmission: `
Section 2 Readmission
---
2 >= 7d <= 30d @initial
ND >= 7d @release
!2, !3, !17A *?
2 >= 7d @readmit
---
measure: Time to readmission: release -> readmit
measure: Initial detention length: initial.start -> initial.end
`.trim(),

  section2ToSection3: `
Section 2 to Section 3 Conversion
---
2 >= 1d @s2
3 >= 1d @s3
---
measure: Time to conversion: s2.start -> s3.start
`.trim(),

  shortTermHolding: `
Short-term Holding to Full Section
---
5(4) | 5(2) @holding
2 | 3 | 4 >= 1d @fullSection
---
measure: Time to full section: holding.start -> fullSection.start
`.trim(),

  ctoRecall: `
CTO Recall
---
17A @cto
17A-R @recall
---
measure: CTO duration before recall: cto.start -> recall.start
measure: Recall duration: recall.start -> recall.end
`.trim(),

  ctoMultipleRecalls: `
CTO with Multiple Recalls
---
17A @ctoStart
17A-R + @recalls
17A @ctoEnd
ND @discharge
---
measure: Total CTO duration: ctoStart.start -> ctoEnd.end
count: "Number of Recalls": recalls in ctoStart -> ctoEnd
`.trim(),

  section3Discharge: `
Section 3 to Discharge
---
3 >= 7d @s3
ND @discharge
---
measure: Time on Section 3: s3.start -> s3.end
`.trim(),

  section2Under14Days: `
Section 2 Under 14 Days (rapid conversion)
---
2 < 14d @s2
3 @s3
---
measure: Section 2 duration: s2.start -> s2.end
`.trim(),

  repeatDetention: `
Repeat Detention (any non-short term power)
---
2 | 3 | CTO @first
ND >= 1d @gap
2 | 3 @second
---
measure: Time between detentions: first.end -> second.start
measure: First detention length: first.start -> first.end
`.trim(),

  longStaySection3: `
Long Stay Section 3 (over 6 months)
---
3 >= 180d @longstay
---
measure: Duration: longstay.start -> longstay.end
`.trim(),

  ctoToSection3: `
CTO Breakdown (CTO to Section 3)
---
17A @cto
17A-R ?
3 @s3
---
measure: CTO duration: cto.start -> cto.end
`.trim(),
} satisfies Record<string, string>;

// ============================================================================
// Utilities
// ============================================================================

export type MeasurementBounds = {
  minDate?: Date; // drop measurements anchored before this (junk historical dates)
  maxDate?: Date; // drop measurements anchored/ending after this (future dates)
};

/** Plausibility filter: non-negative value, and dates within bounds if given. */
export function inBounds(
  m: MeasurementResult,
  bounds?: MeasurementBounds,
): boolean {
  if (m.value < 0) return false;
  if (!bounds) return true;
  if (bounds.minDate && m.fromDate < bounds.minDate) return false;
  if (
    bounds.maxDate &&
    (m.fromDate > bounds.maxDate || m.toDate > bounds.maxDate)
  )
    return false;
  return true;
}

export function getMeasurementDistribution(
  matches: PathwayMatch[],
  measurementName: string,
  bounds?: MeasurementBounds,
) {
  // Drop negative values (artifacts) and out-of-range dates (junk timestamps).
  const values = matches
    .flatMap((m) => m.measurements)
    .filter((m) => m.name === measurementName && inBounds(m, bounds))
    .map((m) => m.value)
    .sort((a, b) => a - b);
  if (values.length === 0)
    return { values: [], min: 0, max: 0, mean: 0, median: 0, p25: 0, p75: 0 };
  const sum = values.reduce((a, b) => a + b, 0);
  // Past the guard above, every index below is in range — the `?? 0` only
  // restates the empty-sample answer that guard already returned. Keeping these
  // `number` rather than `number | undefined` matters: the type is exported.
  const at = (index: number): number => values[index] ?? 0;
  return {
    values,
    min: at(0),
    max: at(values.length - 1),
    mean: sum / values.length,
    median: at(Math.floor(values.length / 2)),
    p25: at(Math.floor(values.length * 0.25)),
    p75: at(Math.floor(values.length * 0.75)),
  };
}
